import { useContext, useEffect, useRef } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import AnalyticsContext from './provider';
import { AnalyticsContextValue, EcommerceBaseProperties, Product } from './types';

export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext as any);
  if (!context) throw new Error('useAnalytics must be used within AnalyticsProvider');
  return context as AnalyticsContextValue;
};

/**
 * Hook to get the user_id from Auth0 for analytics tracking
 * Returns user?.sub if authenticated, otherwise undefined
 */
export const useAnalyticsUserId = (): string | undefined => {
  const { isAuthenticated, user, isLoading } = useAuth0();

  // Return undefined while loading or if not authenticated
  if (isLoading || !isAuthenticated) return undefined;

  return user?.sub;
};

export const useProductListTracking = (
  products: Product[],
  listName: string,
  extraProps?: EcommerceBaseProperties
) => {
  const { trackProductListViewed } = useAnalytics();
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Guard: need element and products
    if (!products || products.length === 0) return;

    // Derive a stable key from product IDs and listName (avoid object identity issues)
    const productIdsKey = products.map(p => p.id).join(',');
    const key = `${listName}:${productIdsKey}`;
    const storageKey = `product_list_tracked_${key}`;

    // If already tracked in this session, no setup required
    if (sessionStorage.getItem(storageKey)) return;

    // If elementRef not attached yet, wait until it is attached by polling briefly
    let el = elementRef.current;
    let pollTimer: number | undefined;
    const setupObserver = (targetEl: HTMLDivElement) => {
      // protect against multiple setups
      let didTrack = false;
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !didTrack) {
              // Double-check session storage
              if (!sessionStorage.getItem(storageKey)) {
                trackProductListViewed(products, listName, extraProps);
                sessionStorage.setItem(storageKey, 'true');
                didTrack = true;
              }
            }
          });
        },
        { threshold: 0.5 }
      );

      observer.observe(targetEl);

      return () => {
        observer.disconnect();
      };
    };

    const start = () => {
      el = elementRef.current;
      if (el) return setupObserver(el);
      // try again for a short period (element may mount a tick later)
      let attempts = 0;
      return new Promise<() => void | undefined>(resolve => {
        pollTimer = window.setInterval(() => {
          attempts += 1;
          el = elementRef.current;
          if (el || attempts > 20) {
            if (pollTimer) window.clearInterval(pollTimer);
            if (el) {
              const cleanup = setupObserver(el);
              resolve(cleanup);
            } else {
              // resolve with noop cleanup
              resolve(() => undefined);
            }
          }
        }, 50);
      });
    };

    let cleanupFn: (() => void) | undefined;
    const maybeCleanupPromise = start();
    if (maybeCleanupPromise && (maybeCleanupPromise as Promise<any>).then) {
      (maybeCleanupPromise as Promise<any>).then((c: any) => {
        cleanupFn = c;
      });
    } else {
      cleanupFn = maybeCleanupPromise as any;
    }

    return () => {
      if (pollTimer) window.clearInterval(pollTimer);
      if (cleanupFn) cleanupFn();
      // keep sessionStorage so we don't retrack across unmount/mount during same session
      // (removeItem previously caused repeated/triple tracking)
    };
  }, [products, listName, extraProps, trackProductListViewed]);

  return elementRef;
};
